/**
 * @file src/sync/noteRates.js
 * @description Turning a musical GRID into a frequency — what the Cosmic LFO needs to run at a
 * rate instead of at a number in Hz.
 *
 * The vocabulary itself is NOT defined here. It is the design library's `QUANTIZE_PRESETS`, the
 * same list the launch grid and the edit snap already pick from, so the whole instrument names
 * subdivisions one way ([[no-two-sources-of-truth]]). This file only resolves a preset id to beats
 * (bar lengths answer to the meter; note values are absolute quarter-beats) and then to Hz.
 *
 * `none` is filtered out: it means "do not quantize", which is not a rate anything can run at.
 *
 * Nothing here is stateful and nothing runs per frame — a rate becomes a frequency when the user
 * picks one or when the tempo moves.
 */

import {
  QUANTIZE_PRESETS,
  quantizeStepBeats,
} from 'plantasia.space-design/react/timeline';
import { DEFAULT_METER_ID, sharedBeatsPerBar } from './meter.js';

/**
 * The rates the LFO offers — every quantize preset except `none`, in the library's own order
 * (slowest first). Triplets come along for free, and are worth having on a modulator.
 *
 * These ids are PERSISTED per dimension and travel on the wire, so REMOVING one from the library's
 * vocabulary strands every saved selection that names it and every peer still sending it — the
 * restore and the remote apply both validate first, so the rate silently falls back and two clients
 * end up modulating at different speeds. Adding is safe; removing needs a migration. (An id this
 * client does not know is deliberately ignored rather than applied, so a NEWER peer's choice leaves
 * this one on the rate it already had instead of on nothing.)
 */
export const NOTE_RATES = Object.freeze(
  QUANTIZE_PRESETS.filter((preset) => preset.id !== 'none'),
);

/** The rate a fresh pick starts on — one bar, the same default the launch grid takes. */
export const DEFAULT_NOTE_RATE_KEY = '1bar';

const BY_KEY = new Map(NOTE_RATES.map((rate) => [rate.id, rate]));

/** The rate with this key, or null. */
export function noteRate(key) {
  return BY_KEY.get(key) ?? null;
}

/** Is this a rate we can run at? (`none` is a preset but not a rate.) */
export function isNoteRateKey(key) {
  return BY_KEY.has(key);
}

/**
 * A rate's length in quarter-note beats, resolved against the meter — a bar is four quarter-beats
 * in 4/4 and three in 6/8, so a rate named after the bar has to follow the bar.
 * @returns {number|null} null for an unknown key.
 */
export function noteRateBeats(key, meterId = DEFAULT_METER_ID) {
  if (!BY_KEY.has(key)) return null;
  const perBar = sharedBeatsPerBar(meterId);
  const beats = quantizeStepBeats(key, Number.isFinite(perBar) && perBar > 0 ? perBar : 4);
  return Number.isFinite(beats) && beats > 0 ? beats : null;
}

/**
 * The frequency a rate runs at — one cycle per `beats`, at this tempo.
 *
 * Deliberately NOT clamped here: the caller folds into the range it owns, so a rate too fast to
 * play becomes its nearest playable relative rather than flattening against a ceiling.
 *
 * @returns {number|null} Hz, or null when the rate or the tempo can't produce one.
 */
export function noteRateToHz(key, bpm, meterId = DEFAULT_METER_ID) {
  const beats = noteRateBeats(key, meterId);
  if (!Number.isFinite(beats) || beats <= 0) return null;
  if (!Number.isFinite(bpm) || bpm <= 0) return null;
  return bpm / (60 * beats);
}
